import { PortableText } from "next-sanity";
import { PortableTextComponents } from "../portableText/PortableTextComponents";
import { LinkElement } from "../link/link";

import { LinkType } from "../../../sanity/lib/types/link";
import { ImageAsset } from "../../../sanity/lib/types/imageAsset";
import React from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ImageElement } from "../Image";

gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

export const StackingCard = ({
    title,
    uid,
    description,
    images,
    subitems,
    link,
    index,
    titleSize,
}: {
    title: string;
    uid: string;
    description?: any;
    images?: Array<ImageAsset>;
    subitems: Array<string>;
    link?: LinkType;
    index: number;
    titleSize?: "small";
}) => {
    const divContainer = React.useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            const images = gsap.utils.toArray<HTMLElement>(`.uid-${uid}-image`);

            if (images.length <= 1) return;

            // alle Bilder erstmal unsichtbar setzen
            gsap.set(images, { autoAlpha: 0 });
            gsap.set(images[0], { autoAlpha: 1 });

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: ".stacking-card",
                    start: "top center",
                    toggleActions: "play none none none",
                },
                repeat: -1,
            });

            images.forEach((img) => {
                tl.to(img, { autoAlpha: 1, duration: 0 }).to(
                    img,
                    { autoAlpha: 0, duration: 0 },
                    "+=1",
                ); // nach kurzer Zeit wieder ausblenden
            });
        },
        { scope: divContainer },
    );

    return (
        <div ref={divContainer}>
            <div
                className={`stacking-card top-0 left-0 w-full bg-[var(--bg-color)] transition-[background-color] duration-300 ease-in-out card md:pb-8`}
            >
                <div className="container pt-6 bg-[var(--bg-color)] transition-[background-color] duration-300 ease-in-out border-t md:pb-16 card-inner">
                    <div className="mx-2.5 md:mx-5">
                        <h3
                            className={`relative flex gap-2 pl-8 md:pl-6 md:ml-72 ${titleSize === "small" ? "m-headline-3 xl:d-headline-1 whitespace-pre-line" : "xl:d-display-1 m-display-2 "}`}
                        >
                            <span className="absolute left-0 md:relative m-body-l md:d-headline-4 -top-3">
                                0{index + 1}
                            </span>
                            {title}
                        </h3>
                        <div
                            className={`grid grid-cols-4 md:grid-cols-12 ${titleSize === "small" ? "pt-6" : "pt-7.5"}  md:pt-20 pb-10 md:pb-0 items-center md:items-start`}
                        >
                            <div className="flex flex-col justify-between h-full col-span-4 md:col-span-6 md:col-start-1">
                                <div className="flex flex-col justify-between flex-grow gap-10 md:gap-6">
                                    <div className="pl-8 m-body-l md:d-body-l md:pl-0">
                                        {description && (
                                            <PortableText
                                                value={description}
                                                components={PortableTextComponents}
                                            />
                                        )}
                                    </div>
                                    <div className="pt-4 md:pt-6 grid grid-cols-2 gap-0.5 border-t pl-8 md:pl-0">
                                        {subitems?.map((category, index) => (
                                            <p
                                                className="col-span-2 md:col-span-1 m-body-l"
                                                key={`category-${index}`}
                                            >
                                                {category}
                                            </p>
                                        ))}
                                    </div>
                                </div>

                                {link && link.label && (
                                    <div className="items-center justify-start hidden mt-10 md:flex">
                                        <LinkElement link={link} className="link" />
                                    </div>
                                )}
                            </div>

                            {link && link.label && (
                                <div className="flex items-center justify-start col-span-4 mt-10 md:hidden">
                                    <LinkElement link={link} className="link" />
                                </div>
                            )}

                            <div className="relative col-span-4 mt-10 overflow-hidden md:col-start-8 md:col-span-5 2xl:col-start-9 2xl:col-span-4 md:mt-0">
                                {images?.map((image, imgIndex) => (
                                    <React.Fragment key={imgIndex}>
                                        {image.image && (
                                            <ImageElement
                                                key={imgIndex}
                                                width={800}
                                                src={image.image}
                                                alt={image.alt || ""}
                                                className={`w-full uid-${uid}-image h-auto object-cover aspect-[355.00/199.69] md:aspect-[570.67/321.00] ${imgIndex === 0 ? "relative" : "absolute top-0 left-0 w-full h-full"}`}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
